'use client';

/**
 * Vysledky rozhovoru pod hero na homepage.
 *
 * POZOR: nezamienat s VoxResults.tsx - to je Juliusov komponent so zoznamom
 * a mapou pre VoxStage. Tento je jednoduchy zoznam pod hero, ktory si vytahuje
 * data sam z enginu cez kontext.
 *
 * Ponuky boli povodne vo vodorovnom pase priamo v smart liste. Tam ich bolo malo
 * vidiet a karta sa tym zvacsovala - preto su teraz tu, pod hero, ako zoznam pod
 * sebou. Nad zoznamom su kriteria, aby bolo jasne, na zaklade coho vznikol.
 *
 * Zobrazuje sa len pocas rozhovoru. Ked ziadny nebezi, sekcia sa nevykresli
 * a stranka vyzera presne ako predtym.
 */

import { cn } from '@/shadcn/lib/utils';
import { MOTION } from '@/shared/components/ui/motion';
import { useVoxCtx } from '../lib/VoxProvider';

const fmt = (n: number) => new Intl.NumberFormat('sk-SK').format(n);

export function VoxHomeResults() {
    const vox = useVoxCtx();
    if (!vox) return null;

    const bezi = vox.phase !== 'idle' || vox.messages.length > 0;
    if (!bezi) return null;

    const prazdno = vox.results.length === 0;

    return (
        <section className="w-full bg-[#fbfbf9] py-[34px] md:py-[44px]">
            <div className="mx-auto w-full max-w-[1180px] px-[16px] md:px-[24px]">
                <div className="flex flex-wrap items-baseline justify-between gap-[10px]">
                    <h2 className="text-[19px] font-[700] text-[#1a1a1a] md:text-[22px]">
                        {prazdno ? 'Zatiaľ nič nesedí' : 'Sedí na kritériá'}
                    </h2>
                    {!prazdno && (
                        <span className="text-[13px] text-black/45">
                            {vox.results.length === 1 ? '1 ponuka' : vox.results.length + ' ponúk'}
                        </span>
                    )}
                </div>

                {/* Kriteria, na zaklade ktorych zoznam vznikol */}
                {vox.criteria.length > 0 && (
                    <div className="mt-[12px] flex flex-wrap gap-[6px]">
                        {vox.criteria.map((c) => (
                            <span
                                key={c.key}
                                className={cn(
                                    'rounded-[16px] border border-[#f0dc8a] bg-[#fff8dc] px-[11px] py-[5px]',
                                    'text-[12px] text-[#1a1a1a]',
                                )}
                            >
                                {c.label}
                            </span>
                        ))}
                    </div>
                )}

                {prazdno ? (
                    <p className="mt-[16px] text-[14px] text-black/55">
                        Skús povedať, čo môžeme uvoľniť - napríklad zvýšiť rozpočet alebo rozšíriť lokalitu.
                    </p>
                ) : (
                    <div className="mt-[18px] flex flex-col gap-[10px]">
                        {vox.results.map((r) => (
                            <a
                                key={r.id}
                                href={'/vox-api/listing/' + r.slug}
                                target="_blank"
                                rel="noopener"
                                className={cn(
                                    'flex items-center gap-[14px] rounded-[16px] border border-[#e8e8e8] bg-white',
                                    'p-[10px] no-underline hover:border-[#f0dc8a] hover:shadow-[0_8px_22px_0_rgba(0,0,0,0.07)]',
                                    'md:gap-[18px] md:p-[12px]',
                                    MOTION,
                                )}
                            >
                                {r.photo ? (
                                    <img
                                        src={r.photo}
                                        alt=""
                                        width={140}
                                        height={100}
                                        className="h-[76px] w-[104px] shrink-0 rounded-[11px] object-cover md:h-[92px] md:w-[132px]"
                                    />
                                ) : (
                                    <span className="h-[76px] w-[104px] shrink-0 rounded-[11px] bg-black/[0.05] md:h-[92px] md:w-[132px]" />
                                )}

                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-[14px] font-[600] text-[#1a1a1a] md:text-[15px]">
                                        {r.title}
                                    </span>
                                    <span className="mt-[2px] block truncate text-[13px] text-black/50">
                                        {r.locality}
                                    </span>
                                    <span className="mt-[5px] block text-[12px] text-black/45">
                                        {[r.area ? r.area + ' m2' : null, r.rooms ? r.rooms + ' izby' : null]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </span>
                                </span>

                                <span className="shrink-0 text-right">
                                    <span className="block text-[17px] font-[700] text-[#1a1a1a] md:text-[19px]">
                                        {fmt(r.price)} €
                                    </span>
                                    {r.area ? (
                                        <span className="block text-[11px] text-black/40">
                                            {fmt(Math.round(r.price / r.area))} € / m2
                                        </span>
                                    ) : null}
                                </span>
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
